import { DragDropEvent } from "services/dragdrop/DragDropContext";
import { AvailabilityByDayType, EventsByResourceType, EventsListType, EventType, IndividualJobEmployeeEvent, SchedulerDraggableId, SchedulerDroppableId } from "../../SchedulingTypes";
import { saveData } from "services/api/saveData";
import { formatDate } from "services/utils/dateTimeUtils";
import { t } from "i18next";
import { calculateDurationMinutesFromDateTimes, determineNearestTimeSlot, generateTimeKeyFromDateTimeString } from "../../functions/schedulingDateUtils";
import { convertUTCToLocalDateTimeString } from "internationalization/timezoneConversions";

export const handleAvailableEmployeeDrag = async (
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>,
    userLocale: string,
    userTimezone: string,
    setFloatingAlert: Function,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
    setAvailabilityByDay: React.Dispatch<React.SetStateAction<AvailabilityByDayType>>
) => {

    // Get the values from the drag result
    const { source, destination, draggableId } = dragResult;

    if (!destination) return;

    // Get the source type, destination type and draggable type
    const sourceType = source?.droppableId?.type;
    const destinationType = destination.droppableId.type;

    // Handle the drag of an available employee event
    if (draggableId.type === 'availableEmployee') {

        // Show alert when dragging to another available employees day column
        if (sourceType === 'availableEmployeesRow' && destinationType === 'availableEmployeesRow') {

            // Only allow dragging from and to the same day
            if (draggableId.event.dayKey !== destination.droppableId.day) {
                setFloatingAlert({
                    type: "danger",
                    message: t('scheduling.scheduling_board.cannot_drag_to_another_day')
                });
                return;
            }
        }

        // Handle drag from the available employees row to a project job day column
        if (sourceType === 'availableEmployeesRow' && destinationType === 'projectJob') {

            // Get the source id
            const sourceResourceId = source?.droppableId?.resourceId;
            if (!sourceResourceId) return;

            // Get the values of the draggable event
            const { employeeId, employeeName } = draggableId.event;

            // Get the destination values
            const { resourceId: destinationJobId, day: destinationDay, project_number } = destination.droppableId;

            // Only allow dragging from and to the same day
            if (draggableId.event.dayKey !== destinationDay) {
                setFloatingAlert({
                    type: "danger",
                    message: t('scheduling.scheduling_board.cannot_drag_to_another_day')
                });
                return;
            }

            try {
                // Get the original event
                const eventKey = draggableId.event.key;

                // Assign the employee to the project job in the backend
                const response = await saveData({
                    apiUrl: `project_scheduler_assign_employee_project_job`,
                    method: 'post',
                    data: {
                        user_id: employeeId,
                        job_id: destinationJobId,
                        day: destinationDay,
                    }
                })

                // Update the event state
                if (response?.status === 200) {

                    // Remove the original event from the available employees row
                    setAvailabilityByDay(prev => {
                        const updated = { ...prev };

                        if (!updated[destinationDay]) return prev;

                        const day = { ...updated[destinationDay] };

                        if (day[employeeId]) {
                            day[employeeId] = day[employeeId].filter(event => event.key !== eventKey);
                            if (day[employeeId].length === 0) {
                                delete day[employeeId];
                            }
                        }

                        updated[destinationDay] = day;

                        return updated;
                    });

                    // Get the data from the response
                    const appointmentId = response.data.id;
                    const status = response.data.status;

                    // Convert datetimes into local times
                    const startDateTime = convertUTCToLocalDateTimeString(response.data.start_datetime, userLocale, userTimezone);
                    const endDateTime = convertUTCToLocalDateTimeString(response.data.end_datetime, userLocale, userTimezone);

                    // Generate event key
                    const key = `projectJobEmployeeEvent-${appointmentId}-${destinationJobId}-${employeeId}`;

                    // Create the new event
                    const newEvent: EventType = {
                        key,
                        type: "projectJobEmployeeEvent",
                        appointmentId,
                        assigneeId: employeeId,
                        resourceId: destinationJobId,
                        startDateTime,
                        endDateTime,
                        dayKey: destinationDay,
                        startTime: generateTimeKeyFromDateTimeString(startDateTime),
                        endTime: generateTimeKeyFromDateTimeString(endDateTime),
                        durationMinutes: calculateDurationMinutesFromDateTimes(startDateTime, endDateTime),
                        assigneeName: employeeName,
                        status,
                        deleted: false
                    };

                    // Remove the old and add the event to the events state
                    setEvents(prev => {
                        const updated = { ...prev };
                        delete updated[eventKey];
                        updated[key] = newEvent;
                        return updated;
                    })

                    // Add the event to the new resource state
                    setEventsByResource(prev => {
                        const updated = { ...prev };

                        // Create a new array for the destination resource if it doesn't exists
                        if (!updated[destinationJobId]) {
                            updated[destinationJobId] = [];
                        }

                        // Add the event key to the resource
                        updated[destinationJobId].push(key);

                        return updated;
                    });

                    // Remove the availability of this user after assigning them to a project
                    // This is not totally properly, but we assume that projects are for a whole
                    // day
                    setAvailabilityByDay(prev => {
                        // Copy the current state
                        const newAvailabilityByDay = { ...prev };

                        // Get the data from the new availability
                        const { user_id, date, blocks } = response.data.new_availability;

                        // Convert the date to a sting
                        const dateKey = String(date);

                        // Add the day to the availability if it does not yet exists
                        if (!newAvailabilityByDay[dateKey]) {
                            newAvailabilityByDay[dateKey] = {};
                        }

                        // Remove the whole availability
                        newAvailabilityByDay[dateKey][user_id] = [];

                        return newAvailabilityByDay;
                    });

                    // Show floating alert on success
                    setFloatingAlert({
                        type: "success",
                        message: t('scheduling.scheduling_board.available_employee_dragged_to_project', { 
                            employee: employeeName,
                            project_number,
                            date: formatDate(destinationDay, userLocale, 'writtenMonth'), 
                        })
                    });
                }
            } catch (error: any) {
                // Show alert if the user has a conflicting appointment at the same time
                if (error?.response?.status === 409) {
                    setFloatingAlert({
                        type: "danger",
                        message: t(`scheduling.scheduling_board.employee_already_scheduled`)
                    });
                    return;
                }

                // Show alert for other errors
                setFloatingAlert({
                    type: "danger",
                    message: t('scheduling.scheduling_board.employee_scheduling_error')
                });
            }
        }

        // Handle drag from the available employees row to a single job day column
        if (sourceType === 'availableEmployeesRow' && destinationType === 'individualJob') {

            // Get the source id
            const sourceResourceId = source?.droppableId?.resourceId;
            if (!sourceResourceId) return;

            // Get the values of the draggable event
            const { employeeId, employeeName } = draggableId.event;

            // Get the destination values
            const { resourceId: destinationJobId, day: destinationDay, timeSlot, job_number } = destination.droppableId;

            // Only allow dragging from and to the same day
            if (draggableId.event.dayKey !== destinationDay) {
                setFloatingAlert({
                    type: "danger",
                    message: t('scheduling.scheduling_board.cannot_drag_to_another_day')
                });
                return;
            }

            try {
                // Get the original event
                const eventKey = draggableId.event.key;

                // Assign the employee to the project job in the backend
                const response = await saveData({
                    apiUrl: `project_scheduler_assign_employee_individual_job`,
                    method: 'post',
                    data: {
                        user_id: employeeId,
                        job_id: destinationJobId,
                        day: destinationDay,
                        time_slot: timeSlot,
                    }
                })

                // Update the event state
                if (response?.status === 200) {

                    // Remove the original event from the available employees row
                    setEventsByResource(prev => {
                        const updated = { ...prev };
                        updated[sourceResourceId] = updated[sourceResourceId].filter(key => key !== eventKey);
                        return updated;
                    });

                    // Get the data from the response
                    const appointmentId = response.data.id;
                    const status = response.data.status;

                    // Convert datetimes into local times
                    const startDateTime = convertUTCToLocalDateTimeString(response.data.start_datetime, userLocale, userTimezone);
                    const endDateTime = convertUTCToLocalDateTimeString(response.data.end_datetime, userLocale, userTimezone);

                    // Convert the datetimes into time keys
                    const startTime = generateTimeKeyFromDateTimeString(startDateTime);
                    const endTime = generateTimeKeyFromDateTimeString(endDateTime);

                    // Generate event key
                    const key = `individualJobEmployeeEvent-${response.data.id}-${destinationJobId}-${employeeId}`;

                    // Create the new event
                    const newEvent: EventType = {
                        key,
                        type: "individualJobEmployeeEvent",
                        appointmentId,
                        assigneeId: employeeId,
                        resourceId: destinationJobId,
                        startDateTime,
                        endDateTime,
                        dayKey: destinationDay,
                        startTime,
                        endTime,
                        durationMinutes: calculateDurationMinutesFromDateTimes(startDateTime, endDateTime),
                        timeSlot: determineNearestTimeSlot(startDateTime),
                        assigneeName: employeeName,
                        status,
                        deleted: false
                    };

                    // Remove the old and add the event to the events state
                    setEvents(prev => {
                        // Copy the current state
                        const updated = { ...prev };

                        // Delete the old event
                        delete updated[eventKey];

                        // Add the new event
                        updated[key] = newEvent;

                        // Loop through the events of this job to add the stack index
                        const slotEvents: IndividualJobEmployeeEvent[] = [];
                        for (const eventK of Object.keys(updated)) {
                            const eventItem = updated[eventK];

                            if (eventItem.type === "individualJobEmployeeEvent" &&
                                eventItem.resourceId === destinationJobId &&
                                eventItem.dayKey === destinationDay &&
                                eventItem.timeSlot === newEvent.timeSlot
                            ) {
                                slotEvents.push(eventItem as IndividualJobEmployeeEvent);
                            }
                        }

                        // Sort the items and set the stack index
                        slotEvents
                            .sort((a, b) => (a.startDateTime ?? "").localeCompare(b.startDateTime ?? ""))
                            .forEach((eventItem, index) => {
                                eventItem.stackIndex = index;
                            });

                        return updated;
                    })

                    // Add the event to the new resource state
                    setEventsByResource(prev => {
                        const updated = { ...prev };

                        // Create a new array for the destination resource if it doesn't exists
                        if (!updated[destinationJobId]) {
                            updated[destinationJobId] = [];
                        }

                        // Add the event key to the resource
                        updated[destinationJobId].push(key);

                        return updated;
                    });

                    // Update the availability events of the user
                    setAvailabilityByDay(prev => {
                        // Copy the current state
                        const newAvailabilityByDay = { ...prev };

                        // Get the data from the new availability
                        const { user_id, date, blocks } = response.data.new_availability;

                        // Convert the date to a sting
                        const dateKey = String(date);

                        // Add the date to the new availability by day state if it does not exists
                        if (!newAvailabilityByDay[dateKey]) newAvailabilityByDay[dateKey] = {};

                        // Create new events for the user
                        const newEvents = blocks.map((block: { start: string, end: string }) => {

                            // Generate local start and end datetimes
                            const startDateTime = `${dateKey}T${block.start}:00`;
                            const endDateTime = `${dateKey}T${block.end}:00`;

                            // Generate a new event key
                            const key = `availableEmployee-${user_id}-${dateKey}-${block.start}`;

                            return {
                                key,
                                type: "availableEmployee",
                                resourceId: "available_employees",
                                startDateTime,
                                endDateTime,
                                dayKey: dateKey,
                                startTime: block.start,
                                endTime: block.end,
                                durationMinutes: calculateDurationMinutesFromDateTimes(startDateTime, endDateTime),
                                timeSlot: determineNearestTimeSlot(startDateTime),
                                employeeId: user_id,
                                employeeName,
                                deleted: false
                            };
                        });

                        // Replace availability for this user/day
                        newAvailabilityByDay[dateKey][user_id] = newEvents;

                        return newAvailabilityByDay;
                    });

                    // Show floating alert on success
                    setFloatingAlert({
                        type: "success",
                        message: t('scheduling.scheduling_board.available_employee_dragged_to_individual_job', { 
                            employee: employeeName,
                            job_number,
                            date: formatDate(destinationDay, userLocale, 'writtenMonth'), 
                            time: determineNearestTimeSlot(startDateTime),
                        })
                    });
                }
            } catch (error: any) {
                // Show alert if the user has a conflicting appointment at the same time
                if (error?.response?.status === 409) {
                    setFloatingAlert({
                        type: "danger",
                        message: t(`scheduling.scheduling_board.employee_already_scheduled`)
                    });
                    return;
                }

                // Show alert for other errors
                setFloatingAlert({
                    type: "danger",
                    message: t('scheduling.scheduling_board.employee_scheduling_error')
                });
            }
        }
    }
}